import { hasValue } from "./general";

export enum SortTypeEnum {
	none = 0,
	ascending = 1,
	descending = -1,
}

export function cmp<T = any>(a: T, b: T) {
	if (a > b) return 1;
	if (a < b) return -1;
	return 0;
}

/** Comparing properties on object type */
export const cmpProp =
	<TObj, TProp extends TObj[keyof TObj]>(propFn: (obj: TObj) => TProp) =>
	(a: TObj, b: TObj) =>
		cmp(propFn(a), propFn(b));

export function sortItem<T>(sortType: SortTypeEnum, a: T, b: T) {
	switch (sortType) {
		case SortTypeEnum.none:
			return 0;
		case SortTypeEnum.ascending:
			return cmp(a, b);
		// invert for descending
		case SortTypeEnum.descending:
			return -cmp(a, b);
	}
}

export const sortItemByPropFn =
	<TObj, TProp extends keyof TObj = any>(propFn: (obj: TObj) => TObj[TProp]) =>
	(sortType: SortTypeEnum) =>
	(a: TObj, b: TObj) =>
		sortItem(sortType, propFn(a), propFn(b));

export const sortFn = sortItemByPropFn<any, any>((t) => t);

export interface SortDefinition<TObj> {
	propFn?: (obj: TObj) => any;
	sortType?: SortTypeEnum;
}

/**
 * Sorts in order
 * @param sortDefinitions Definitions of inidividual sorts
 */
export function sortOrder<TObj>(...sortDefinitions: SortDefinition<TObj>[]) {
	const defaultFn = (a: TObj, b: TObj) => 0;
	if (!sortDefinitions) return defaultFn;
	sortDefinitions = sortDefinitions.filter(hasValue);
	sortDefinitions.forEach((sd) => {
		if (!hasValue(sd.propFn)) sd.propFn = (t) => t;
	});
	sortDefinitions.forEach((sd) => {
		if (!hasValue(sd.sortType)) sd.sortType = SortTypeEnum.ascending;
	});
	const sortFns = sortDefinitions.map((sd) => sortItemByPropFn(sd.propFn)(sd.sortType));
	return sortFns.reduce((previous, current) => (a, b) => previous(a, b) || current(a, b), defaultFn);
}
